import { useState, useEffect } from "react";
import { AlertTriangle, X, Trash2, Loader2 } from "lucide-react";

/**
 * Hardened account deletion confirmation modal.
 *
 * Requires user to:
 *   1. Type "ELIMINAR" exactly to enable the delete button
 *   2. Confirm a checkbox acknowledging data loss is permanent
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onConfirm: () => Promise<void>  — caller performs DELETE /auth/account
 *   busy: boolean
 */
const CONFIRM_WORD = "ELIMINAR";

export default function DeleteAccountModal({ open, onClose, onConfirm, busy }) {
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (!open) {
      setTyped("");
      setAck(false);
    }
  }, [open]);

  if (!open) return null;

  const canDelete = typed === CONFIRM_WORD && ack && !busy;
  const handleClose = busy ? undefined : () => onClose?.();

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="delete-account-modal"
      onClick={handleClose}
    >
      <div
        className="glass-card rounded-md max-w-md w-full p-6 sm:p-8 border-2 border-red-900/60 relative"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(135deg, rgba(127,29,29,0.08), rgba(13,12,11,0.95))",
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          disabled={busy}
          aria-label="Cerrar"
          className="absolute top-3 right-3 text-faded hover:text-red-400 p-1 disabled:opacity-30"
          data-testid="delete-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        <AlertTriangle className="w-7 h-7 text-red-400 mb-4" strokeWidth={1.5} />
        <h3 className="font-display text-2xl text-red-300 mb-2">Eliminar mi cuenta</h3>
        <p className="text-sm text-muted leading-relaxed mb-5">
          Esta acción es <strong className="text-red-300">irreversible</strong>. Se borrarán
          permanentemente: tu bóveda cifrada, mensajes programados, herederos, activos digitales,
          contactos de confianza, historias y todos tus archivos. No podremos recuperarlo.
        </p>

        <label className="font-label text-faded block mb-2 text-xs">
          Escribe <span className="text-red-400 font-bold">{CONFIRM_WORD}</span> para confirmar:
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value.toUpperCase())}
          placeholder={CONFIRM_WORD}
          className="input-field mb-4 font-mono tracking-widest"
          autoComplete="off"
          autoFocus
          data-testid="delete-modal-confirm-input"
          disabled={busy}
        />

        <label className="flex items-start gap-2 mb-5 text-xs text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            disabled={busy}
            className="accent-red-500 mt-0.5"
            data-testid="delete-modal-ack-checkbox"
          />
          <span className="leading-snug">
            Entiendo que perderé todos mis datos permanentemente y que recibiré un email de
            confirmación tras el borrado (RGPD Art. 17).
          </span>
        </label>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="btn-ghost text-sm"
            data-testid="delete-modal-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canDelete}
            className="btn-danger text-sm"
            data-testid="delete-modal-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {busy ? "Eliminando…" : "Eliminar definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}
